/**
 * Standardized Application Error Structure
 */
export class AppError extends Error {
    constructor({ code, message, type = 'toast', detail = null }) {
        super(message);
        this.code = code;         // Unique error identifier (e.g., 'QUOTA_EXCEEDED')
        this.message = message;   // Clean, user-facing copy ready for the UI
        this.type = type;         // UI Presentation rule: 'modal' or 'toast'
        this.detail = detail;     // Raw debug metadata for developers
    }
}

export const ErrorHandler = {
    /**
     * Sanitizes any unhandled exception into a predictable AppError object
     * @param {any} error - The caught exception
     * @returns {AppError}
     */
    handle(error) {
        // 1. If it's already an instance of AppError, pass it straight through
        if (error instanceof AppError) {
            return error;
        }

        console.error("🚨 [ErrorHandler Caught Exception]:", error);

        // 2. Extract structured server-side errors passed through stringified catch blocks
        try {
            const parsed = JSON.parse(error.message);
            if (parsed && parsed.code) {
                return this.fromServerCode(parsed.code, parsed.message || parsed.error, parsed.detail);
            }
        } catch {
            // Error message is a plain string; proceed to standard resolution
        }

        // 3. Handle physical browser network dropouts / timeouts
        if (error.name === 'TypeError' && error.message.toLowerCase().includes('fetch')) {
            return new AppError({
                code: 'NETWORK_DISCONNECTED',
                message: 'Network connection failed. Please check your internet settings and try again.',
                type: 'toast'
            });
        }

        // 4. Ultimate generic fallback
        return new AppError({
            code: 'UNKNOWN_ERROR',
            message: error.message || 'Something went wrong. Please try again later.',
            type: 'toast',
            detail: error
        });
    },

    /**
     * Maps business error codes to specific UI presentation behaviors
     */
    fromServerCode(code, serverMessage, detail = null) {
        switch (code) {
            case 'AI_QUOTA_EXCEEDED':
                return new AppError({
                    code,
                    message: serverMessage || 'Your AI generation quota has been reached for today. Upgrade your plan to get unlimited magic.',
                    type: 'modal', // Quota limits are high-value monetization triggers; use a Modal
                    detail
                });

            case 'UNAUTHORIZED':
                return new AppError({
                    code,
                    message: 'Your session has expired. Please log in again.',
                    type: 'toast',
                    detail
                });

            case 'SERVER_ISSUE':
            case 'SERVER_ERROR':
                return new AppError({
                    code,
                    message: 'The AI core engine is currently busy. Please try again in a moment.',
                    type: 'toast',
                    detail
                });

            case 'INPUT_REQUIRED':
                return new AppError({
                    code,
                    message: 'Content cannot be empty. Please enter some text.',
                    type: 'toast',
                    detail
                });

            default:
                return new AppError({
                    code: code || 'SERVER_UNKNOWN',
                    message: serverMessage || 'An unexpected server exception occurred.',
                    type: 'toast',
                    detail
                });
        }
    }
};